<template>
    <div class="mt-6 content your-enrollment">
        <h3 class="has-text-white"><i class="fad fa-fw fa-id-badge mr-3"></i>Your Enrollment</h3>

        <p class="mt-6 has-text-white" v-if="loading"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading enrollment...</p>
        <b-message type="is-danger" class="mt-6" v-else-if="error">{{ error }}</b-message>

        <div v-else class="mt-6 pb-6">
            <ui-card>
                <div class="details-box">
                    <div class="detail">
                        <div class="header">Course</div>
                        <div class="info">
                            <div>{{ details.course.title }}</div>
                            <div class="sub" v-if="details.course.provider">Provided by {{ details.course.provider }}</div>
                        </div>
                    </div>
                    <div class="detail" v-if="details.custom_name">
                        <div class="header">Enrollment Name</div>
                        <div class="info">{{ details.custom_name }}</div>
                    </div>
                    <div class="detail" v-if="details.certification">
                        <div class="header">Certification</div>
                        <div class="info">
                            <div>{{ details.certification.title || 'Certification' }}<span v-if="details.tier && details.tier.units"> &middot; {{ details.tier.units }}-hour tier</span></div>
                            <div class="sub" v-if="details.hours.cap !== null && details.tier && details.hours.cap !== details.tier.units">This enrollment is limited to {{ details.hours.max }} clock hours.</div>
                        </div>
                    </div>
                    <div class="detail" v-if="details.hours.max > 0">
                        <div class="header">Clock Hours</div>
                        <div class="info">
                            <div>{{ details.hours.logged }} of {{ details.hours.max }} hours logged<span v-if="details.hours.remaining > 0 && !details.completed_at"> &middot; {{ details.hours.remaining }} remaining</span></div>
                            <div class="sub" v-if="details.hours.awarded > 0">{{ details.hours.awarded }} hours awarded on a certification application.</div>
                            <div class="sub" v-if="details.clock_hour_fees.length">Clock hour fee: {{ details.clock_hour_fees[0].unit_price | currency }} per clock hour, billed as hours are completed.</div>
                        </div>
                    </div>
                    <div class="detail">
                        <div class="header">Enrolled On</div>
                        <div class="info">{{ details.created_at | moment('MMMM D, YYYY') }}</div>
                    </div>
                    <div class="detail" v-if="details.course.type === 'self_directed'">
                        <div class="header">Study Plan</div>
                        <div class="info">
                            <span v-if="details.started_at">Approved {{ details.started_at | moment('MMMM D, YYYY') }}</span>
                            <span v-else-if="details.setup_status === 'submitted'">Submitted, pending approval</span>
                            <span v-else-if="details.setup_status === 'changes_requested'">Changes requested</span>
                            <span v-else>Not yet submitted</span>
                            <span v-if="details.milestones.total"> &middot; {{ details.milestones.approved }} of {{ details.milestones.total }} milestones accepted</span>
                        </div>
                    </div>
                    <div class="detail">
                        <div class="header">Status</div>
                        <div class="info">
                            <template v-if="details.completed_at">Completed {{ details.completed_at | moment('MMMM D, YYYY') }}</template>
                            <template v-else-if="details.is_expired">Expired {{ details.expires_on | expireDate }}</template>
                            <template v-else>
                                Active<span v-if="details.expires_on"> &middot; expires {{ details.expires_on | expireDate }}</span>
                                <div class="sub" v-if="details.lock.locked">{{ lockText }}</div>
                            </template>
                        </div>
                    </div>

                    <div class="detail">
                        <div class="header">Payment</div>
                        <div class="info">
                            <!-- Directly related to a sale: the transaction(s) with receipts -->
                            <template v-if="details.payment.transactions.length">
                                <div class="txn" v-for="txn in details.payment.transactions" :key="txn.id">
                                    <div>
                                        {{ txn.label }} &middot; {{ txn.total_amount | currency }} on {{ txn.date | moment('MMMM D, YYYY') }}
                                        <span class="sub is-inline" v-if="txn.covers_enrollments"> (covers {{ txn.covers_enrollments }} enrollments)</span>
                                    </div>
                                    <div class="mt-2 mb-2" v-if="txn.receipt_available">
                                        <b-button @click="viewReceipt(txn)" type="is-info" size="is-small" :loading="receiptLoading === txn.id"><i class="fad fa-file-invoice-dollar mr-2"></i>View Receipt</b-button>
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="details.payment.source === 'plan' && details.payment.plan">
                                <div>Deferred Payment Agreement<span v-if="details.payment.plan.nickname"> &middot; {{ details.payment.plan.nickname }}</span></div>
                                <div class="sub">Serviced by our lending partner, The Payment Option (TPO). Payment history and receipts are on their site.</div>
                                <div class="mt-2 mb-2">
                                    <b-button v-if="details.payment.plan.agreement_url" tag="a" :href="details.payment.plan.agreement_url" target="_blank" rel="noopener" type="is-warning" size="is-small"><i class="fas fa-file-signature mr-2"></i>Sign Agreement<i class="fas fa-arrow-up-right-from-square ml-2"></i></b-button>
                                    <b-button v-else tag="a" :href="tpoUrl" target="_blank" rel="noopener" type="is-info" size="is-small"><i class="fad fa-credit-card mr-2"></i>Manage Payment Plan<i class="fas fa-arrow-up-right-from-square ml-2"></i></b-button>
                                </div>
                            </template>
                            <template v-else-if="details.payment.source === 'reallocation' && details.lineage.reallocated_from">
                                <div>Created from hours reallocated out of <router-link :to="'/course/' + details.lineage.reallocated_from.id + '/information/enrollment'" v-if="!details.lineage.reallocated_from.is_deleted">{{ details.lineage.reallocated_from.name }}</router-link><span v-else>{{ details.lineage.reallocated_from.name }}</span><span v-if="details.lineage.reallocated_at"> on {{ details.lineage.reallocated_at | moment('MMMM D, YYYY') }}</span>.</div>
                                <div class="sub">Any payment record stays with the original enrollment.</div>
                            </template>
                            <template v-else-if="details.payment.source === 'reactivation' && details.lineage.reactivated_from">
                                <div>Created when <router-link :to="'/course/' + details.lineage.reactivated_from.id + '/information/enrollment'">{{ details.lineage.reactivated_from.name }}</router-link> was reactivated.</div>
                            </template>
                            <template v-else-if="details.payment.source === 'legacy'">
                                <div>Imported from your previous account records.</div>
                                <div class="sub">Receipts from before the move are not available here. Contact support@my247ed.com if you need one.</div>
                            </template>
                            <template v-else>
                                <div>No payment record on file for this enrollment.</div>
                                <div class="sub">Enrollments arranged by purchase order, by a representative, or at no charge appear this way.</div>
                            </template>
                            <div class="sub support-note">Need an itemized receipt or invoice? Contact <a href="mailto:support@my247ed.com">support@my247ed.com</a>.</div>
                        </div>
                    </div>

                    <div class="detail" v-if="details.lineage.reallocated_to.length">
                        <div class="header">Hours Reallocated</div>
                        <div class="info">
                            <div v-for="run in details.lineage.reallocations" :key="run.id" class="mb-2">
                                <div>{{ run.moved_hours }} hours moved on {{ run.date | moment('MMMM D, YYYY') }} &middot; {{ run.kept_hours }} kept here</div>
                                <ul class="sub-list">
                                    <li v-for="item in run.items" :key="item.enrollment_id">
                                        <router-link :to="'/course/' + item.enrollment_id">{{ (item.enrollment && item.enrollment.name) || item.name || 'New enrollment' }}</router-link> &mdash; {{ item.hours }} hours
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="detail" v-if="details.lineage.reactivated_to">
                        <div class="header">Reactivated</div>
                        <div class="info">Carried forward into <router-link :to="'/course/' + details.lineage.reactivated_to.id">{{ details.lineage.reactivated_to.name }}</router-link><span v-if="details.lineage.reactivated_at"> on {{ details.lineage.reactivated_at | moment('MMMM D, YYYY') }}</span>.</div>
                    </div>
                </div>
            </ui-card>

            <!-- Reallocate Hours -->
            <ui-card class="mt-5" v-if="details.course.type === 'self_directed'">
                <h4 class="title is-5 mb-4"><i class="fad fa-fw fa-arrows-turn-right mr-2 has-text-primary"></i>Reallocate Hours</h4>
                <p class="mb-4">Unused clock hours in this enrollment can be moved into one or more new course enrollments. Hours you have already logged stay here.</p>

                <div class="hours-grid">
                    <div class="hours-tile">
                        <div class="num">{{ reallocation.pool_hours }}</div>
                        <div class="lbl">Total Hours</div>
                    </div>
                    <div class="hours-tile">
                        <div class="num">{{ reallocation.logged_hours }}</div>
                        <div class="lbl">Hours Logged</div>
                    </div>
                    <div class="hours-tile" :class="{'is-available': reallocation.eligible}">
                        <div class="num">{{ reallocation.max_move_hours }}</div>
                        <div class="lbl">Available to Move</div>
                    </div>
                </div>

                <div class="not-eligible mt-4" v-if="!reallocation.eligible">
                    <i class="far fa-circle-info mr-2"></i>{{ reallocation.reason }}
                </div>

                <template #footer v-if="reallocation.eligible">
                    <div class="is-flex is-justify-content-flex-end">
                        <b-button tag="router-link" :to="{name: 'course-reallocate', params: {enrollmentId: details.id}}" type="is-primary" size="is-small"><i class="fas fa-arrows-turn-right mr-2"></i>Reallocate Hours</b-button>
                    </div>
                </template>
            </ui-card>

            <removal-card class="mt-5" kind="enrollment" :endpoint="`app/enrollments/${details.id}/removal`" :name="details.display_name" :payment-note="removalPaymentNote" @removed="onRemoved"/>
        </div>
    </div>
</template>

<script>
    import moment from "moment";
    import UiCard from "../../components/UiCard.vue";
    import RemovalCard from "../../components/RemovalCard.vue";
    import {client as http} from "../../http_client";
    import {TPO_URL} from "../../utils/paymentPlans";

    const LOCK_TEXT = {
        expired: 'This enrollment has expired.',
        payment: 'Locked until the payment agreement is completed.',
        legacy_payment: 'On hold pending a payment agreement from your previous enrollment.',
        setup: 'Waiting on study plan approval before course work can begin.',
        milestone: 'A milestone reflection must be accepted before you can continue.'
    };

    /**
     * "Your Enrollment" — the course counterpart of the conference "Your
     * Registration" page: everything we know about the enrollment, the sale
     * it came from (with receipts when Stripe has one), its hour lineage,
     * and the entry point to Reallocate Hours.
     */
    export default {
        name: "YourEnrollment",
        components: {UiCard, RemovalCard},
        props: ['enrollmentId'],
        data() {
            return {
                loading: true,
                error: null,
                details: null,
                reallocation: null,
                receiptLoading: null,
                tpoUrl: TPO_URL
            };
        },
        computed: {
            lockText() {
                return LOCK_TEXT[this.details.lock.reason] || 'This enrollment is currently locked.';
            },
            removalPaymentNote() {
                const txns = this.details.payment.transactions;
                if (!txns.length) return '';
                if (txns.some(t => t.covers_enrollments)) {
                    return 'This enrollment was part of a purchase covering several enrollments; removing it does not change or refund that purchase.';
                }
                const paid = txns.reduce((sum, t) => sum + t.total_amount, 0);
                return `The ${this.$options.filters.currency(paid)} paid for this enrollment will not be refunded.`;
            }
        },
        filters: {
            // expires_on is a DATE column — format in UTC so it never slips a day
            expireDate(value) {
                return value ? moment.utc(value).format('MMMM D, YYYY') : '';
            }
        },
        watch: {
            enrollmentId() {
                this.load();
            }
        },
        methods: {
            load() {
                this.loading = true;
                this.error = null;
                http.get(`app/enrollments/${this.enrollmentId}/details`, {force: true}).then(response => {
                    this.details = response.data.enrollment;
                    this.reallocation = response.data.reallocation;
                    this.loading = false;
                }).catch(err => {
                    this.error = (err.response && err.response.data && err.response.data.error) || 'Could not load this enrollment.';
                    this.loading = false;
                });
            },
            onRemoved() {
                this.$store.dispatch('getEnrollments');
                this.$router.push({name: 'dashboard'});
            },
            viewReceipt(txn) {
                this.receiptLoading = txn.id;
                http.get(`app/enrollments/${this.enrollmentId}/receipts/${txn.id}`, {force: true}).then(response => {
                    this.receiptLoading = null;
                    if (response.data.url) window.open(response.data.url, '_blank');
                }).catch(err => {
                    this.receiptLoading = null;
                    this.$buefy.dialog.alert({
                        title: 'Receipt Unavailable',
                        message: (err.response && err.response.data && err.response.data.error) || 'No receipt is available for this payment.',
                        type: 'is-warning',
                        confirmText: 'OK'
                    });
                });
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .details-box {
        background-color: #fdfdfd;
        border-radius: 8px;
        border: 1px solid #e0e2e8;

        .detail {
            padding: 0.5rem 1rem;
            border-bottom: 1px solid #e0e2e8;

            &:last-child {
                border-bottom: none;
            }

            .header {
                font-size: 1rem;
                color: #224C74;
                font-weight: bold;
            }

            .info {
                font-size: 1.1rem;

                .sub {
                    font-size: 0.85rem;
                    color: #7a8a9d;

                    &.is-inline {
                        display: inline;
                    }
                }

                .sub-list {
                    margin: 0.25rem 0 0 1.2rem;
                    font-size: 0.95rem;
                    list-style: disc;
                }

                .txn + .txn {
                    margin-top: 0.5rem;
                    padding-top: 0.5rem;
                    border-top: 1px dashed #e0e2e8;
                }

                .support-note {
                    margin-top: 0.5rem;
                }
            }
        }
    }

    .hours-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px;

        @media (max-width: 600px) {
            grid-template-columns: 1fr;
        }

        .hours-tile {
            background: #f4f6f9;
            border: 1px solid #e6eaef;
            border-radius: 8px;
            padding: 0.9rem 1rem;
            text-align: center;

            .num {
                font-size: 1.6rem;
                font-weight: 900;
                color: #33475b;
                line-height: 1.1;
            }

            .lbl {
                font-size: 0.75rem;
                font-weight: bold;
                letter-spacing: 0.04em;
                text-transform: uppercase;
                color: #7a8a9d;
                margin-top: 0.25rem;
            }

            &.is-available .num {
                color: #2e7d4f;
            }
        }
    }

    .not-eligible {
        color: #666d7a;
        font-size: 0.95rem;
    }
</style>
